import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiFehler, schicke } from '../lib/api'
import { Button, Sheet, TextField, useToast } from '../ui'

export interface LoeschbaresMitglied {
  id: string
  displayName: string
}

export function MitgliedLoeschenSheet({
  offen,
  onSchliessen,
  onGeloescht,
  mitglied,
}: {
  offen: boolean
  onSchliessen: () => void
  onGeloescht: () => void
  mitglied: LoeschbaresMitglied | null
}) {
  const qc = useQueryClient()
  const zeigeToast = useToast()
  const [bestaetigung, setBestaetigung] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    if (offen) {
      setBestaetigung('')
      setFehler(null)
    }
  }, [offen, mitglied?.id])

  const loeschen = useMutation({
    mutationFn: () =>
      schicke('DELETE', `/api/members/${mitglied?.id ?? ''}`, {
        confirmation: bestaetigung,
      }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['members'] }),
        qc.invalidateQueries({ queryKey: ['me'] }),
      ])
      zeigeToast(`„${mitglied?.displayName ?? 'Person'}“ wurde endgültig gelöscht`, 'erfolg')
      onGeloescht()
    },
    onError: (f: unknown) => {
      const meldung = f instanceof ApiFehler ? f.message : 'Endgültiges Löschen fehlgeschlagen.'
      setFehler(meldung)
      zeigeToast(meldung, 'error')
    },
  })

  if (!mitglied) return null
  const stimmt = bestaetigung === mitglied.displayName

  return (
    <Sheet offen={offen} titel="Person endgültig löschen" onSchliessen={onSchliessen}>
      <div className="stack-4">
        <p>
          Diese Aktion kann nicht rückgängig gemacht werden. Zugang, Berechtigungen, Zuweisungen,
          Erledigungen, Stundenplan und Taschengeldverlauf dieser Person werden entfernt. Gemeinsame
          Einträge können ohne Namenszuordnung erhalten bleiben.
        </p>
        <p>
          Gib zur Bestätigung <strong>{mitglied.displayName}</strong> ein.
        </p>
        <TextField
          id="mitglied-loeschen-name"
          label="Name zur Bestätigung"
          wert={bestaetigung}
          onWert={setBestaetigung}
          autoComplete="off"
          autoFocus
        />
        {fehler ? (
          <p className="fehler" role="alert">
            {fehler}
          </p>
        ) : null}
        <div className="blatt__fuss-inline">
          <Button variant="text" onClick={onSchliessen}>
            Abbrechen
          </Button>
          <Button
            className="btn--gefahr"
            laeuft={loeschen.isPending}
            disabled={!stimmt}
            onClick={() => loeschen.mutate()}
          >
            Endgültig löschen
          </Button>
        </div>
      </div>
    </Sheet>
  )
}
